import { Button, FormControl, Input, InputLabel, Select } from '@material-ui/core';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import TextField from '@material-ui/core/TextField';
import React, { useEffect, useReducer, useRef } from 'react';
import {
  BiomarkerData,
  UserSubmittedLabResultsDetailsResponse,
} from '../../../interfaces/user-submitted-lab-result';
import useStyles from '../routes/user-submitted-lab-results-details/UserSubmittedLabResultDetails.styles';

interface UserSubmittedLabResultsTableProps {
  biomarkerData: BiomarkerData;
  userSubmittedLabResult: UserSubmittedLabResultsDetailsResponse;
  formSubmittedByIsLoggedIn: boolean;
  handleSave: (newStatus?: string, rejectedReason?: string, rejectedComment?: string, rejectedReasonTitle?: string) => void;
}

const UserSubmittedLabResultRejectDialog: React.FC<UserSubmittedLabResultsTableProps> = ({
  biomarkerData,
  userSubmittedLabResult,
  handleSave,
  formSubmittedByIsLoggedIn,
}) => {
  const classes = useStyles();
  const [open, setOpen] = React.useState(false);
  const [code, setCode] = React.useState<string>(userSubmittedLabResult.rejectedReason || '');
  const [title, setTitle] = React.useState<string>('');
  const [codeValid, setCodeValid] = React.useState<boolean>(false);
  const [description, setDescription] = React.useState<string>(
    userSubmittedLabResult.rejectedComment || ''
  );
  const [descriptionValid, setDescriptionValid] = React.useState<boolean>(false);
  const showValidationErrors = useRef(false);
  const [, forceUpdate] = useReducer(x => x + 1, 0);

  useEffect(() => {
    validateForm();
  }, [code, description]);

  const handleSelect = (event: React.ChangeEvent<{ value: unknown }>) => {
    const code = String(event.target.value) || '';
    setCode(code);
    setDescription(
      biomarkerData?.rejectReasons.find(reason => reason.code === code)?.description || ''
    );
    setTitle(
      biomarkerData?.rejectReasons.find(reason => reason.code === code)?.title || ''
    );
    showValidationErrors.current = true;
  };

  const handleClickOpen = () => {
    setOpen(true);
  };

  const validateForm = () => {
    if (code === undefined || code === '') {
      setCodeValid(false);
    } else {
      setCodeValid(true);
    }

    if (description === undefined || description === '' || description.length > 1000) {
      setDescriptionValid(false);
    } else {
      setDescriptionValid(true);
    }
  };

  const handleReject = async () => {
    showValidationErrors.current = true;
    if (codeValid && descriptionValid) {
      await handleSave('rejected', code, title, description);
      setOpen(false);
      return;
    }
    forceUpdate();
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <div>
      {!formSubmittedByIsLoggedIn &&
        userSubmittedLabResult.status !== 'completed' &&
        userSubmittedLabResult.status !== 'rejected' && (
          <Button className={classes.rejectButton} onClick={handleClickOpen} variant="outlined">
            Reject
          </Button>
        )}
      <Dialog open={open} onClose={handleClose} aria-labelledby="form-dialog-title">
        <DialogTitle id="form-dialog-title">Reject Results</DialogTitle>
        <DialogContent>
          <form className={classes.dialogContainer}>
            <FormControl className={classes.formControl} fullWidth={true}>
              <InputLabel focused htmlFor="demo-dialog-native">
                Select reason...
              </InputLabel>
              <Select
                native
                value={code}
                onChange={handleSelect}
                input={<Input id="demo-dialog-native" />}
                className={classes.rejectDialogSelect}
                required
                error={showValidationErrors.current && !codeValid}
              >
                <option aria-label="None" value="" />
                {biomarkerData?.rejectReasons.map((reason, index) => {
                  return (
                    <option key={index} value={reason.code}>
                      {reason.title}
                    </option>
                  );
                })}
              </Select>
              <TextField
                id="outlined-basic"
                label="Description"
                variant="outlined"
                value={description}
                multiline={true}
                placeholder="Enter description..."
                rows={5}
                rowsMax={5}
                className={classes.rejectDialogTextBox}
                onChange={event => {
                  showValidationErrors.current = true;
                  setDescription(event.target.value);
                }}
                required
                error={showValidationErrors.current && !descriptionValid}
              ></TextField>
            </FormControl>
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} className={classes.cancelButton} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={() => {
              handleReject();
            }}
            disabled={!codeValid || !descriptionValid}
            className={classes.rejectButton}
            variant="outlined"
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default UserSubmittedLabResultRejectDialog;
